/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  Flex,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
  VStack,
  Icon,
  IconButton,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { useRef, useState, useMemo } from 'react';
import ImageIcon from '@/assets/svg/image-upload.svg?react';
import { ViewIcon } from '@chakra-ui/icons';
import { useGetSizeGuides } from '@/services/addProduct';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

interface ImageOption {
  id: string;
  src: string;
  label: string;
}

interface ImageUploaderFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  defaultImage?: string;
  predefinedImages?: ImageOption[];
  label?: string;
  categoryId?: string | number;
  currentValue?: string;
}

const MAX_FILE_SIZE = 512 * 1024;

export default function ImageUploaderField<T extends FieldValues>({
  name,
  control,
  defaultImage,
  label,
  categoryId,
}: ImageUploaderFieldProps<T>) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string>('');
  const [fileError, setFileError] = useState(false);
  const [page, setPage] = useState(1);

  const [dragCounterMain, setDragCounterMain] = useState(0);
  const [dragCounterModal, setDragCounterModal] = useState(0);

  const isDraggingOverMain = dragCounterMain > 0;
  const isDraggingOverModal = dragCounterModal > 0;

  const handleOpen = () => {
    setPage(1);
    onOpen();
  };

  const shouldFetchCategoryGuides = !!categoryId;

  const { data: paginatedSizeGuides, isFetching } = useGetSizeGuides(
    shouldFetchCategoryGuides ? Number(categoryId) : undefined,
    page,
    { enabled: isOpen },
  );

  const dynamicPredefined = useMemo(() => {
    if (shouldFetchCategoryGuides && paginatedSizeGuides?.results) {
      return paginatedSizeGuides.results.map((guide: any) => ({
        id: guide.pk.toString(),
        src: guide.image,
        label: guide.name,
      }));
    }

    if (!shouldFetchCategoryGuides && paginatedSizeGuides?.results) {
      return paginatedSizeGuides.results.map((guide: any) => ({
        id: guide.pk.toString(),
        src: guide.image,
        label: guide.name,
      }));
    }

    return [];
  }, [shouldFetchCategoryGuides, paginatedSizeGuides]);

  const shouldShowPagination = paginatedSizeGuides && paginatedSizeGuides.page_count > 1;

  const openPreview = (src: string) => {
    setPreviewSrc(src);
    setPreviewOpen(true);
  };

  const imageToDataUrl = (src: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      img.crossOrigin = 'anonymous';
      const url = src + (src.includes('?') ? '&' : '?') + 't=' + Date.now();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas 2D context not available'));
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = url;
    });
  };

  const handleImageSelect = async (src: string, onChange: (value: string) => void) => {
    try {
      const dataUrl = await imageToDataUrl(src);
      onChange(dataUrl);
      setSelectedImage(dataUrl);
    } catch (err) {
      console.warn('Canvas conversion failed, using raw URL as fallback', err);
      onChange(src);
    } finally {
      onClose();
    }
  };

  const processFile = (
    file: File,
    onChange: (value: string) => void,
    closeModal: boolean = true,
  ) => {
    if (!file.type.startsWith('image/')) {
      setFileError(true);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError(true);
      return;
    }

    setFileError(false);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      onChange(result);
      setSelectedImage(result);
      if (closeModal) onClose();
    };
    reader.readAsDataURL(file);
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file, onChange);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent, area: 'main' | 'modal') => {
    e.preventDefault();
    e.stopPropagation();
    if (area === 'main') {
      setDragCounterMain((prev) => prev + 1);
    }
    if (area === 'modal') {
      setDragCounterModal((prev) => prev + 1);
    }
  };

  const handleDragLeave = (e: React.DragEvent, area: 'main' | 'modal') => {
    e.preventDefault();
    e.stopPropagation();
    if (area === 'main') {
      setDragCounterMain((prev) => prev - 1);
    }
    if (area === 'modal') {
      setDragCounterModal((prev) => prev - 1);
    }
  };

  const handleDrop = (
    e: React.DragEvent,
    onChange: (value: string) => void,
    area: 'main' | 'modal',
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (area === 'main') setDragCounterMain(0);
    if (area === 'modal') setDragCounterModal(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      processFile(file, onChange, area === 'modal');
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const currentImage = field.value || defaultImage;
        const showPlaceholder = !field.value && !defaultImage;

        return (
          <Box w="100%">
            {label && (
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                {label}
              </Text>
            )}

            <Box display="flex" alignItems="center" h={{ base: '150px', lg: '170px' }}>
              <Box
                tabIndex={0}
                role="button"
                aria-label="Open size guide image selector"
                cursor="pointer"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                border="2px dashed"
                borderColor={isDraggingOverMain ? 'brand.primary.DEFAULT' : 'gray.400'}
                borderRadius="md"
                p={0}
                position="relative"
                transition="all 0.2s"
                _hover={{ bg: 'gray.100', borderColor: 'brand.primary.DEFAULT' }}
                _focusVisible={{
                  borderColor: 'brand.primary.DEFAULT',
                  boxShadow: '0 0 0 3px brand.primary.200',
                  outline: 'none',
                  zIndex: 1,
                }}
                w="100%"
                maxW={{ base: '100%', lg: '300px' }}
                h="100%"
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={handleOpen}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpen();
                  }
                }}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, 'main')}
                onDragLeave={(e) => handleDragLeave(e, 'main')}
                onDrop={(e) => handleDrop(e, field.onChange, 'main')}
              >
                <Box position="relative" boxSize="100%" borderRadius="md" overflow="hidden">
                  {showPlaceholder ? (
                    <Flex
                      direction="column"
                      align="center"
                      justify="center"
                      gap={3}
                      h="100%"
                      w="100%"
                      p={4}
                      textAlign="center"
                    >
                      <Icon as={ImageIcon} w={12} h={12} color="gray.400" />
                      <Text
                        color="brand.primary.DEFAULT"
                        textDecor="underline"
                        fontWeight="medium"
                        cursor="pointer"
                        _hover={{ textDecor: 'none' }}
                      >
                        Click to upload
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        or drag and drop
                      </Text>
                      {isDraggingOverMain && (
                        <Text fontSize="md" color="brand.primary.DEFAULT" fontWeight="bold">
                          Drop image here
                        </Text>
                      )}
                    </Flex>
                  ) : (
                    <Image
                      src={currentImage}
                      alt="Size guide preview"
                      objectFit="cover"
                      boxSize="100%"
                    />
                  )}
                  {hover && currentImage && (
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      w="full"
                      h="full"
                      bg="rgba(0,0,0,0.5)"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      gap={2}
                    >
                      <Button
                        variant="outline"
                        bg="white"
                        size="sm"
                        borderColor="brand.primary.DEFAULT"
                        color="brand.primary.DEFAULT"
                        _hover={{ bg: 'brand.primary.DEFAULT', color: 'white' }}
                      >
                        Change Image
                      </Button>
                      <IconButton
                        aria-label="Preview"
                        icon={<ViewIcon />}
                        size="sm"
                        bg="white"
                        borderColor="brand.primary.DEFAULT"
                        color="brand.primary.DEFAULT"
                        _hover={{ bg: 'brand.primary.DEFAULT', color: 'white' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openPreview(currentImage);
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Modal isOpen={isOpen} onClose={onClose} size="xl">
              <ModalOverlay />
              <ModalContent maxW="90vw">
                <ModalHeader>Select Size Guide Image</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                  <VStack spacing={5}>
                    <Text fontSize="sm" color="gray.600">
                      {shouldFetchCategoryGuides
                        ? dynamicPredefined.length > 0
                          ? 'Choose from category-specific size guides'
                          : 'No size guides for this category — upload your own'
                        : 'Choose from all size guides'}
                    </Text>

                    {dynamicPredefined.length > 0 ? (
                      <SimpleGrid columns={{ base: 2, sm: 3, md: 5 }} spacing={4} w="100%">
                        {dynamicPredefined.map((img) => (
                          <Box
                            key={img.id}
                            borderRadius="md"
                            overflow="hidden"
                            cursor="pointer"
                            border="2px solid"
                            borderColor={
                              selectedImage === img.src ? 'brand.primary.DEFAULT' : 'transparent'
                            }
                            onClick={() => handleImageSelect(img.src, field.onChange)}
                            _hover={{ borderColor: 'brand.primary.DEFAULT' }}
                            position="relative"
                            minH="120px"
                          >
                            <Image
                              src={img.src}
                              alt={img.label}
                              w="100%"
                              h="100px"
                              objectFit="cover"
                            />
                            <Text fontSize="xs" p={1} textAlign="center" bg="gray.50" noOfLines={1}>
                              {img.label}
                            </Text>
                            <IconButton
                              aria-label="Preview"
                              icon={<ViewIcon />}
                              size="xs"
                              position="absolute"
                              top={1}
                              right={1}
                              bg="brand.primary.DEFAULT"
                              opacity={0.85}
                              _hover={{ opacity: 1 }}
                              color="white"
                              onClick={(e) => {
                                e.stopPropagation();
                                openPreview(img.src);
                              }}
                            />
                          </Box>
                        ))}
                      </SimpleGrid>
                    ) : (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                        {isFetching ? 'Loading...' : 'No size guides available'}
                      </Text>
                    )}

                    {shouldShowPagination && (
                      <Flex justify="space-between" align="center" mt={4} w="100%" gap={2}>
                        <Button
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          isDisabled={isFetching || !paginatedSizeGuides.previous}
                          leftIcon={<ChevronLeftIcon />}
                          variant="outline"
                          flex={1}
                        >
                          Previous
                        </Button>
                        <Text fontSize="sm" fontWeight="medium" minW="80px" textAlign="center">
                          Page {page} of {paginatedSizeGuides.page_count}
                        </Text>
                        <Button
                          size="sm"
                          onClick={() => setPage((p) => p + 1)}
                          isDisabled={isFetching || !paginatedSizeGuides.next}
                          rightIcon={<ChevronRightIcon />}
                          variant="outline"
                          flex={1}
                        >
                          Next
                        </Button>
                      </Flex>
                    )}

                    <Box w="100%">
                      <Text fontSize="sm" color="gray.500" mb={2} textAlign="center">
                        Or upload your own size guide
                      </Text>
                      {fileError && (
                        <Alert status="error" mb={2} fontSize="sm">
                          <AlertIcon />
                          File must be an image and less than 512 KB.
                        </Alert>
                      )}
                      <Box
                        tabIndex={0}
                        role="button"
                        aria-label="Upload custom size guide image"
                        cursor="pointer"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        border="2px dashed"
                        borderColor={isDraggingOverModal ? 'brand.primary.DEFAULT' : 'gray.400'}
                        borderRadius="md"
                        p={4}
                        _hover={{ bg: 'gray.100', borderColor: 'brand.primary.DEFAULT' }}
                        _focusVisible={{
                          borderColor: 'brand.primary.DEFAULT',
                          boxShadow: '0 0 0 3px brand.primary.200',
                          outline: 'none',
                          zIndex: 1,
                        }}
                        minH="100px"
                        onClick={() => fileInputRef.current?.click()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            fileInputRef.current?.click();
                          }
                        }}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, 'modal')}
                        onDragLeave={(e) => handleDragLeave(e, 'modal')}
                        onDrop={(e) => handleDrop(e, field.onChange, 'modal')}
                      >
                        <Icon as={ImageIcon} w={8} h={8} color="gray.500" mb={2} />
                        <Text color="brand.primary.DEFAULT" textDecor="underline">
                          Click to upload
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          or drag and drop
                        </Text>
                        {isDraggingOverModal && (
                          <Text
                            fontSize="md"
                            color="brand.primary.DEFAULT"
                            fontWeight="bold"
                            mt={2}
                          >
                            Drop image here
                          </Text>
                        )}
                      </Box>
                      <Input
                        id="custom-upload-modal"
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={(e) => handleFileUpload(e, field.onChange)}
                        display="none"
                      />
                    </Box>
                  </VStack>
                </ModalBody>
              </ModalContent>
            </Modal>

            <Modal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} size="xl">
              <ModalOverlay />
              <ModalContent>
                <ModalCloseButton />
                <ModalBody p={0}>
                  <Image
                    src={previewSrc}
                    alt="Full size guide"
                    w="100%"
                    maxH="80vh"
                    objectFit="contain"
                  />
                </ModalBody>
              </ModalContent>
            </Modal>
          </Box>
        );
      }}
    />
  );
}
